import { Injectable } from "@angular/core";
import { BehaviorSubject, forkJoin, Observable } from "rxjs";
import { ServerSetting } from "../models/map/server-setting";
import { HttpClientService } from "./httpclient";
import { environment } from "../../../environments/environment";
import { catchError, map, switchMap, take } from "rxjs/operators";
import { throwError } from "rxjs/internal/observable/throwError";
import { Coordinates, RoutePointInformation } from "../models/map/required-route-information";
import { OrderLine } from "../../pb-screen/models/order-line/orderLine.interface";

@Injectable()
export class MapService {

  private isMapVisible: boolean = false;

  show = new BehaviorSubject<boolean>(false);
  info = new BehaviorSubject<string>("");
  orderlines = new BehaviorSubject<OrderLine[]>([]);


  toggleMap(): void {
    this.isMapVisible = !this.isMapVisible;

    this.show.next(this.isMapVisible);
  }

  setOrderlines(orderlines: OrderLine[]): void {
    this.orderlines.next([...orderlines]);
  }

  setInfo(title: string): void {
    this.info.next(title);
  }


  constructor(
    private httpClient: HttpClientService
  ) {
  }

  fetchSettingsFromBusinessCentral(): Observable<ServerSetting[]> {
    return this.httpClient.get(environment.tmsApiEndpointAddress + "map/settings")
      .pipe(
        map((serverSettingsInsideData: any) => serverSettingsInsideData.data as ServerSetting[]),
        catchError((err) => {
          return throwError(err);
        })
      );
  }

  fetchCoordinatesForEntireRoute(requiredRoutePointInformation: RoutePointInformation[]): Observable<Coordinates[][]> {
    return this.fetchSettingsFromBusinessCentral()
      .pipe(
        take(1),
        switchMap((serverSettings: ServerSetting[]) => {
          const obs: Observable<Coordinates[]>[] = [];

          for (let i = 0; i < requiredRoutePointInformation.length - 1; i++) {
            const routePoint1 = requiredRoutePointInformation[i];
            const routePoint2 = requiredRoutePointInformation[i + 1];

            obs.push(this.fetchRoutePolylineCoords(serverSettings, routePoint1, routePoint2));
          }

          // Single OL
          if (requiredRoutePointInformation.length === 1) {
            obs.push(this.fetchRoutePolylineCoords(serverSettings, requiredRoutePointInformation[0], requiredRoutePointInformation[0]));
          }

          return forkJoin(obs);
        }),
        catchError(error => {
          return throwError(error);
        })
      );
  }

  private fetchRoutePolylineCoords(serverSettings: ServerSetting[], routePoint1: RoutePointInformation, routePoint2: RoutePointInformation): Observable<Coordinates[]> {
    return this.httpClient.post(
      "https://azf-g4l-ptv-xserver.azurewebsites.net/api/CalculateDistanceForMap?code=naRfm0c4DmW9OTaa5sFl",
      {
        Latitude1: routePoint1.coordinates.y,
        Longitude1: routePoint1.coordinates.x,
        Latitude2: routePoint2.coordinates.y,
        Longitude2: routePoint2.coordinates.x,
        Config: {
          Settings: serverSettings.map(setting => setting.name),
          Values: serverSettings.map(setting => setting.value)
        }
      }
    ).pipe(
      map(data => {
        // Same Address
        if (!data["plainPoints"]) {
          return [
            {
              x: this.convertDMSToDegrees(routePoint1.coordinates.x),
              y: this.convertDMSToDegrees(routePoint1.coordinates.y)
            }
          ];
        }


        const arr: Coordinates[] = [];

        for (const coords of data["plainPoints"]) {
          arr.push({
            x: this.convertDMSToDegrees(coords.x),
            y: this.convertDMSToDegrees(coords.y)
          });
        }

        return arr;
      }),
      catchError(error => {
        return throwError(error);
      })
    );
  }

  convertDMSToDegrees(coordinate: number): number {
    let tempCoordinate = Math.floor(coordinate / 1000);

    const seconds = (coordinate % 1000) / 10;
    const minutes = tempCoordinate % 100;

    tempCoordinate = Math.floor(tempCoordinate / 100);
    const degrees = tempCoordinate;

    const decimalDegrees = degrees + (minutes / 60) + (seconds / 3600);
    return Math.round(decimalDegrees * 1000000) / 1000000; // JS SUCKS AT PRECISION FIX
  }
}
